import Vue from 'vue'
import Vuex from 'vuex'
import XLSX from 'xlsx'

import { CartItem, Product, SizeType, Section, Param, Page } from '@/interfaces/product'
import { dropbox } from '@/api/dropbox'

Vue.use(Vuex)

/**
 * Definiciones:
 *
 *   page: Es todo número de página controlado por y visible a el usuario
 *   pageIndex: Es el índice de página manejado internamente en el carrusel
 */

const SKIP_LAST_PAGE_INDEX = 2
const savedCart = localStorage.getItem('cart')

export interface AppState {
  cart: CartItem[];
  params: Param[];
  pages: Page[];
  sections: Section[];
  products: Product[];
  sizeTypes: SizeType[];
  totalPages: number;
  rawTotalPages: number;
  currentPageIndex: number;
}

const mediaQuery = window.matchMedia('(max-width: 800px)')

const store = new Vuex.Store<AppState>({
  state: {
    cart: savedCart ? JSON.parse(savedCart) : [],
    params: [],
    pages: [],
    sections: [],
    products: [],
    sizeTypes: [],
    totalPages: 0,
    rawTotalPages: 0,
    currentPageIndex: 0
  },
  getters: {
    cartQuantity ({ cart }) {
      return cart.reduce((sum, { quantity }) => quantity + sum, 0)
    },
    cartTotal ({ cart }) {
      return cart.reduce((sum, { _orig, quantity }) => _orig.Precio * quantity + sum, 0)
    },
    inMobilePages ({ totalPages, rawTotalPages }) {
      return totalPages === rawTotalPages
    },
    currentPage ({ currentPageIndex, rawTotalPages }, getters) {
      const pageIndex = getters.inMobilePages ? currentPageIndex : currentPageIndex * 2

      return Math.min(Math.max(pageIndex, 1), rawTotalPages - SKIP_LAST_PAGE_INDEX)
    }
  },
  mutations: {
    setCurrentPageIndex (state, pageIndex: number) {
      state.currentPageIndex = pageIndex
    },
    setRawTotalPages (state, total: number) {
      state.rawTotalPages = total
    },
    setTotalPages (state, total: number) {
      state.totalPages = total
    },
    emptyCart (state) {
      state.cart = []
    },
    addItem ({ cart }, item: CartItem) {
      const foundItem = cart.find(({ _orig, fullSize }) => _orig.ID === item._orig.ID && fullSize === item.fullSize)

      if (foundItem) {
        foundItem.quantity++
      } else {
        cart.push(item)
      }
    },
    updateItemQuantity ({ cart }, { item, quantity }: { item: CartItem; quantity: number }) {
      if (quantity === 0) {
        // Handle remove item
        cart.splice(cart.indexOf(item), 1)
      } else {
        item.quantity = quantity
      }
    },
    removeItem ({ cart }, item: CartItem) {
      cart.splice(cart.indexOf(item), 1)
    },
    setParams (state, params: Param[]) {
      state.params = params
    },
    setSections (state, sections: Section[]) {
      state.sections = sections
    },
    setPages (state, pages: Page[]) {
      state.pages = pages
    },
    setProducts (state, products: Product[]) {
      state.products = products
    },
    setSizeTypes (state, sizeTypes: SizeType[]) {
      state.sizeTypes = sizeTypes
    }
  },
  actions: {
    setCurrentPageIndexFromPage ({ state, getters, commit }, page: number) {
      // Get index skipping first and last page index
      const normalizedPage = Math.min(Math.max(page, 1), state.rawTotalPages - SKIP_LAST_PAGE_INDEX)
      const pageIndex = getters.inMobilePages || normalizedPage <= 1 ? normalizedPage - (getters.inMobilePages ? 0 : 1) : Math.floor(normalizedPage / 2)

      commit('setCurrentPageIndex', pageIndex)
    },
    async loadConfig (context) {
      const configMetadata = await dropbox.filesDownload({ path: '/config.xlsx' })
      const reader = new FileReader()

      reader.onload = (e: any) => {
        const data = e.target.result
        const workbook = XLSX.read(data, {
          type: 'binary'
        })

        const pages: Page[] = XLSX.utils.sheet_to_json(workbook.Sheets.Paginas)
        const products: Product[] = XLSX.utils.sheet_to_json(workbook.Sheets.Productos)
        const totalPages = pages.length

        context.commit('setPages', pages)
        context.commit('setProducts', products)
        context.commit('setParams', XLSX.utils.sheet_to_json(workbook.Sheets.Otros))
        context.commit('setSections', XLSX.utils.sheet_to_json(workbook.Sheets.Secciones))
        context.commit('setRawTotalPages', totalPages)
        context.commit('setTotalPages', mediaQuery.matches ? totalPages : Math.ceil(totalPages / 2))
        context.commit('setSizeTypes', XLSX.utils.sheet_to_json(workbook.Sheets.TipoTalla))
      }

      reader.readAsBinaryString((configMetadata as any).fileBlob)
    }
  },
  modules: {
  }
})

mediaQuery.addListener(e => {
  store.commit('setTotalPages', e.matches ? store.state.rawTotalPages : Math.ceil(store.state.rawTotalPages / 2))
})

store.subscribe((_, { cart }) => {
  if (cart.length) {
    localStorage.setItem('cart', JSON.stringify(cart))
  } else {
    localStorage.removeItem('cart')
  }
})

export default store
